import classNames from 'classnames';
import React, { useEffect, useMemo } from 'react';
import type {
  ActionMeta,
  ClearIndicatorProps,
  DropdownIndicatorProps,
  GroupBase,
  MenuPlacement,
  MenuProps,
  MultiValue,
  Props as ReactSelectProps,
  SingleValue,
} from 'react-select';
import ReactSelect, { components } from 'react-select';
import type { CreatableProps } from 'react-select/creatable';
import Creatable from 'react-select/creatable';
import Caret from './Caret';
import * as styles from './Select.module.scss';
import SVGIcon from './SVGIcon';
import clearIcon from '../icons/non-prefilled/16x16/clear.svg';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type OptionTypeBase = { [key: string]: any };

type ComposedValueType<Option, IsMulti = false> = IsMulti extends true
  ? MultiValue<Option>
  : SingleValue<Option>;

type SimpleValue<
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
> = IsMulti extends true ? readonly OptionType['value'][] : OptionType['value'];

export type ValueType<
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  IsSimpleValue extends boolean = true,
> = IsSimpleValue extends true
  ? SimpleValue<OptionType, IsMulti>
  : ComposedValueType<OptionType, IsMulti>;

type OnChange<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
  IsSimpleValue extends boolean,
  IsClearable extends boolean,
> = (
  value: IsClearable extends true
    ? ValueType<OptionType, IsMulti, IsSimpleValue> | null
    : ValueType<OptionType, IsMulti, IsSimpleValue>,
  action: ActionMeta<OptionType>,
) => void;

export type SelectProps<
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  IsSimpleValue extends boolean = true,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
  IsClearable extends boolean = false,
> = Omit<
  ReactSelectProps<OptionType, IsMulti, GroupType>,
  'value' | 'onChange' | 'isClearable'
> & {
  selectRef?: React.ComponentProps<
    typeof ReactSelect<OptionType, IsMulti, GroupType>
  >['ref'];
  creatable?: boolean;
  disableMenuPortal?: boolean;
  simpleValue?: IsSimpleValue;
  value?: ValueType<OptionType, IsMulti, IsSimpleValue> | null;
  onChange: OnChange<OptionType, IsMulti, IsSimpleValue, IsClearable>;
  isClearable?: IsClearable;
} & (
    | ({ creatable: true } & Omit<
        CreatableProps<OptionType, IsMulti, GroupType>,
        'value' | 'onChange' | 'isClearable'
      >)
    | { creatable?: false }
  );

const DropdownIndicator = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  props: DropdownIndicatorProps<OptionType, IsMulti, GroupType>,
) => (
  <components.DropdownIndicator {...props}>
    <Caret size="small" />
  </components.DropdownIndicator>
);

const ClearIndicator = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>(
  props: ClearIndicatorProps<OptionType, IsMulti, GroupType>,
) => (
  <components.ClearIndicator {...props}>
    <SVGIcon size="small">{clearIcon}</SVGIcon>
  </components.ClearIndicator>
);

const Menu = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  children,
  ...props
}: MenuProps<OptionType, IsMulti, GroupType>) => (
  <components.Menu {...props}>{children}</components.Menu>
);

const Select = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  IsSimpleValue extends boolean = true,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
  IsClearable extends boolean = false,
>({
  className,
  creatable = false,
  disableMenuPortal = false,
  selectRef,
  options,
  simpleValue = true as IsSimpleValue,
  isMulti,
  ...props
}: SelectProps<OptionType, IsMulti, IsSimpleValue, GroupType, IsClearable>) => {
  const [portalTarget, menuPortalTarget] = useMemo(() => {
    if (disableMenuPortal) return [];

    const container = document.createElement('div');
    container.classList.add(styles.portal, 'ignore-react-onclickoutside');

    const div = document.createElement('div');
    div.classList.add(styles.select);
    if (className) {
      div.classList.add(...className.split(' '));
    }

    container.appendChild(div);
    return [container, div];
  }, [className, disableMenuPortal]);

  useEffect(() => {
    if (portalTarget && !portalTarget.parentNode) {
      document.body.appendChild(portalTarget);
    }

    return () => {
      portalTarget?.parentNode?.removeChild(portalTarget);
    };
  }, [className, portalTarget]);

  const onChange: ReactSelectProps<OptionType, IsMulti>['onChange'] =
    simpleValue
      ? (fullValue, ...args) => {
          const values =
            fullValue &&
            (isMulti
              ? (fullValue as ValueType<OptionType, true>).map(
                  (opt) => opt.value,
                )
              : (fullValue as ValueType<OptionType, false>).value);

          props.onChange?.(values, ...args);
        }
      : (props.onChange as OnChange<OptionType, IsMulti, false, IsClearable>);

  let value: ReactSelectProps<OptionType, IsMulti>['value'];

  if (simpleValue) {
    if (isMulti && Array.isArray(props.value)) {
      value = ((options || []) as OptionType[]).filter((opt) => {
        return props.value?.includes(opt.value);
      });
    } else {
      value =
        props.value == null
          ? props.value
          : ((options || []) as OptionType[]).find(
              (opt) => opt.value === props.value,
            );
    }
  } else {
    value = props.value;
  }

  const reactSelectProps = {
    menuPlacement: 'auto' as MenuPlacement,
    ...props,
    onChange,
    className: classNames(className, styles.select),
    classNamePrefix: 'react-select',
    isMulti,
    options,
    value,
    components: {
      ClearIndicator,
      DropdownIndicator,
      Menu,
      ...(props.components ?? {}),
    },
    menuPortalTarget,
  };

  return creatable ? (
    <Creatable ref={selectRef} {...reactSelectProps} />
  ) : (
    <ReactSelect ref={selectRef} {...reactSelectProps} />
  );
};

export default Select;
